import React, { useMemo, useState } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { FilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { FilterListBoxItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { FilterSearchBox } from 'apps/sigma2-ui/src/filters-v2/components/FilterSearchBox';
import { applyChangesToValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TypographyTextColorVariant } from 'apps/sigma2-ui/src/components/ui/Typography';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export interface SearchableFilterListBoxProps {
  /**
   * Whether you want to allow multiple selections or not
   */
  multiple: boolean;

  /**
   * The items in the list box
   */
  items: FilterListBoxItem[];

  /**
   * Callback when the selected items change
   * If the multiple prop is false, this will be an array with one item at most
   * @param newValues
   */
  onChange: (newValues: string[]) => void;

  color?: TypographyTextColorVariant;

  renderTopSection?: ({
    searchBox,
  }: {
    searchBox: React.ReactNode;
  }) => React.ReactNode;

  disabled?: boolean;
}

export const SearchableFilterListBox = ({
  multiple,
  items,
  onChange,
  renderTopSection,
  disabled,
}: SearchableFilterListBoxProps) => {
  const language = useLanguage();

  const [searchValue, setSearchValue] = useState<string>('');

  const filteredItems = items.filter((item) =>
    getTranslatedContentAsString(item.translatedContent)
      .toLowerCase()
      .includes(searchValue.toLowerCase()),
  );

  const selectedValues = useMemo(
    () => items.filter((item) => item.selected).map((item) => item.value),
    [items],
  );

  const topSection = useMemo(() => {
    const searchBox = (
      <FilterSearchBox
        value={searchValue}
        onChange={(newValue) => setSearchValue(newValue)}
      />
    );

    if (renderTopSection) {
      return renderTopSection({ searchBox });
    }

    return searchBox;
  }, [searchValue, renderTopSection]);

  return (
    <Spacing direction={'vertical'} size={'none'} className={'relative'}>
      <div className={'sticky top-0'}>
        <div className={'-mt-3 -mx-3 p-3 pb-0 bg-white'}>{topSection}</div>
      </div>
      {filteredItems.length > 0 && (
        <FilterListBox
          multiple={multiple}
          items={filteredItems}
          onUpdate={({ addedSelections, removedSelections }) => {
            if (!multiple) {
              onChange(addedSelections);
              return;
            }

            onChange(
              applyChangesToValues({
                selections: selectedValues,
                addedSelections,
                removedSelections,
              }),
            );
          }}
        />
      )}
      {filteredItems.length === 0 && (
        <>
          <div className={'mt-3'} />
          <S2Typography
            variant={'body-7'}
            textColorVariant={'gray-600'}
            translatedContent={language.searchableFilterListBoxEmpty()}
          />
        </>
      )}
      {disabled && (
        <div
          className={'absolute inset-0 bg-white/70 cursor-not-allowed rounded'}
        />
      )}
    </Spacing>
  );
};
